import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { Panel } from '../../../ui-kit';

export interface FlowNode {
  id: string;
  /** What this step is: "Leads in". */
  step: string;
  /** What is doing it: "HubSpot", "Regal". */
  name: string;
  /** One short line under the name. */
  detail?: string;
  logo?: React.ReactNode;
  /** Where clicking it goes. */
  to?: string;
}

/**
 * How the agent is wired, as the headline of the page.
 *
 * The one thing an onlooker should take from Spotlight is that this is joined
 * to real systems at both ends and decides something in the middle. So it is
 * drawn large, with arrows that actually point, rather than as four status
 * cards that happen to sit in a row.
 */
export default function AgentFlow({
  nodes, onOpen, loading = false,
  subtitle = 'From the lead landing in HubSpot to an advisor calling back',
}: { nodes: FlowNode[]; onOpen?: (id: string) => void; loading?: boolean; subtitle?: string }) {
  return (
    <Panel
      title="How it runs"
      subtitle={subtitle}
      loading={loading}
      loadingHeight={96}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems="stretch"
        sx={{ gap: 0 }}
      >
        {nodes.map((node, index) => (
          <React.Fragment key={node.id}>
            <Box
              onClick={onOpen && node.to ? () => onOpen(node.to!) : undefined}
              sx={(t) => ({
                flex: 1,
                minWidth: 0,
                p: 1.5,
                borderRadius: `${t.ds.radius.md}px`,
                border: `1px solid ${t.tokens.border.subtle}`,
                bgcolor: t.tokens.surface.raised,
                cursor: onOpen && node.to ? 'pointer' : 'default',
                transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                '&:hover': onOpen && node.to ? { borderColor: t.tokens.brand.primary } : undefined,
              })}
            >
              <Typography
                variant="overline"
                sx={(t) => ({ color: t.tokens.text.tertiary, display: 'block', mb: 1 })}
              >
                {node.step}
              </Typography>

              {/* Logos on one line across the boxes: pinned to the top, whatever the text's length. */}
              <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ minWidth: 0 }}>
                <Box
                  sx={(t) => ({
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: `${t.ds.radius.sm}px`,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: t.tokens.surface.sunken,
                    color: t.tokens.brand.primary,
                  })}
                >
                  {node.logo}
                </Box>

                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
                    {node.name}
                  </Typography>
                  {node.detail ? (
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
                      {node.detail}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Box>

            {index < nodes.length - 1 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ flexShrink: 0, px: 0.5, py: { xs: 0.5, md: 0 } }}
              >
                <ArrowForwardRounded
                  sx={(t) => ({
                    fontSize: 22,
                    color: t.tokens.brand.primary,
                    transform: { xs: 'rotate(90deg)', md: 'none' },
                  })}
                />
              </Stack>
            ) : null}
          </React.Fragment>
        ))}
      </Stack>
    </Panel>
  );
}
